import { getStorageKey } from "#src/hooks/useStorageKey";
import { ExceptionUtils } from "#src/utils/exception";
import type {
  ConfiguredDataTable,
  ConfiguredDataTableHeader,
} from "@validereinc/common-components";
import {
  getUserInterfaceConfigurationSchema,
  type UserInterfaceConfigurationType,
} from "@validereinc/domain";
import { z } from "zod";

export const ConfiguredDataTableHeaderSchema: z.ZodType<ConfiguredDataTableHeader> =
  z.object({
    key: z.string(),
    isFixed: z.boolean().optional(),
    isSelected: z.boolean().optional(),
    headers: z.lazy(() => ConfiguredDataTableHeaderSchema.array()).optional(),
  });

export const TableUserInterfaceConfigurationSchema = z.object({
  columns: z.array(ConfiguredDataTableHeaderSchema),
});
export type TableUserInterfaceConfigurationType = z.infer<
  typeof TableUserInterfaceConfigurationSchema
>;

export const TableUserInterfaceConfigurationFullSchema =
  getUserInterfaceConfigurationSchema(TableUserInterfaceConfigurationSchema);
export type TableUserInterfaceConfigurationFullType = z.infer<
  typeof TableUserInterfaceConfigurationFullSchema
>;

export const extractTableColumnConfigurationFromTableConfiguration = (
  config: UserInterfaceConfigurationType["configuration"]
): ConfiguredDataTableHeader[] | null => {
  const validationResult =
    TableUserInterfaceConfigurationSchema.safeParse(config);

  if (!validationResult.success) return null;

  return validationResult.data.columns;
};

export const loadDefaultTableConfigurations =
  (
    userId: string,
    companyId: string,
    tableConfigurations?: UserInterfaceConfigurationType[]
  ) =>
  () => {
    if (!tableConfigurations) {
      return Promise.resolve(0);
    }

    const appliedKeys = new Set<string>();
    let appliedCount = 0;

    try {
      tableConfigurations.forEach((configuration) => {
        const { key, visibility, configuration: config } = configuration;

        if (visibility !== "company" || appliedKeys.has(JSON.stringify(key)))
          return;

        const cols =
          extractTableColumnConfigurationFromTableConfiguration(config);

        if (!cols) return;

        appliedKeys.add(JSON.stringify(key));
        appliedCount++;
        window.localStorage.setItem(
          getStorageKey(key, `table-configuration`, userId, companyId),
          JSON.stringify({
            headers: cols,
            source: "user-default",
          } satisfies ConfiguredDataTable)
        );
      });

      return Promise.resolve(appliedCount);
    } catch (err) {
      ExceptionUtils.reportException(err, "warning", {
        sourceComponent: "DefaultUserInterfaceConfigurationProvider",
        hint: "Failed to load default table configurations",
      });
      throw err;
    }
  };

export const clearDefaultTableConfigurations =
  (
    userId: string,
    companyId: string,
    tableConfigurations?: UserInterfaceConfigurationType[]
  ) =>
  () => {
    if (!tableConfigurations) {
      return Promise.resolve(0);
    }

    let clearedCount = 0;

    try {
      tableConfigurations.forEach((configuration) => {
        const { type, key, visibility } = configuration;

        if (visibility !== "company") return;

        clearedCount++;
        window.localStorage.removeItem(
          getStorageKey(key, `${type}-configuration`, userId, companyId)
        );
      });

      return Promise.resolve(clearedCount);
    } catch (err) {
      ExceptionUtils.reportException(err, "warning", {
        sourceComponent: "DefaultUserInterfaceConfigurationProvider",
        hint: "Failed to clear default table configurations",
      });
      throw err;
    }
  };
